import { useEffect } from 'react';
import { Link } from 'react-router-dom';

/** Visionneuse plein écran: Échap pour fermer, flèches du clavier ou boutons pour passer d'une photo à l'autre. */
export default function Lightbox({ images, index, onClose, onChange }: { images: { src: string; alt: string; caption?: string; to?: string }[]; index: number | null; onClose: () => void; onChange: (i: number) => void }) {
  const open = index !== null;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onChange((index! + 1) % images.length);
      if (e.key === 'ArrowLeft') onChange((index! - 1 + images.length) % images.length);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = ''; window.removeEventListener('keydown', onKey); };
  }, [open, index, images.length, onClose, onChange]);

  if (!open || !images[index!]) return null;
  const img = images[index!];
  const btn = 'absolute flex h-12 w-12 items-center justify-center rounded-full border border-white/40 text-white transition-colors hover:border-brand-dark hover:bg-brand-dark';
  return (
    <div role="dialog" aria-modal aria-label="Visionneuse de photos" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4 animate-fade-in" onClick={onClose}>
      <img src={img.src} alt={img.alt} onClick={(e) => e.stopPropagation()} className="max-h-[88vh] max-w-[92vw] object-contain" />
      <button aria-label="Fermer" onClick={onClose} className={`${btn} right-4 top-4`}>
        <svg viewBox="0 0 24 24" className="h-5 w-5" stroke="currentColor" strokeWidth="2" fill="none"><path d="M5 5l14 14M19 5L5 19" /></svg>
      </button>
      {(img.caption || img.to) && (
        <div onClick={(e) => e.stopPropagation()} className="absolute inset-x-0 bottom-0 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 bg-gradient-to-t from-black/80 to-transparent px-6 pb-14 pt-16 text-white md:px-12">
          {img.caption && <p className="max-w-3xl text-[1.0625rem] font-medium leading-snug !text-white">{img.caption}</p>}
          {img.to && <Link to={img.to} onClick={onClose} className="text-[0.875rem] font-semibold uppercase tracking-[0.094rem] text-brand hover:text-white">Voir le projet →</Link>}
        </div>
      )}
      {images.length > 1 && (
        <>
          <button aria-label="Photo précédente" onClick={(e) => { e.stopPropagation(); onChange((index! - 1 + images.length) % images.length); }} className={`${btn} left-4 top-1/2 -translate-y-1/2`}>
            <svg viewBox="0 0 24 12" className="h-3 w-6" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M24 6H1M6 1L1 6l5 5" /></svg>
          </button>
          <button aria-label="Photo suivante" onClick={(e) => { e.stopPropagation(); onChange((index! + 1) % images.length); }} className={`${btn} right-4 top-1/2 -translate-y-1/2`}>
            <svg viewBox="0 0 24 12" className="h-3 w-6" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M0 6h23M18 1l5 5-5 5" /></svg>
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-sm text-white/80 tabular-nums">{index! + 1} / {images.length}</div>
        </>
      )}
    </div>
  );
}
